import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  PointElement,
  LinearScale,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ChartsDisplay = ({
  xValues,
  yValues,
  selectedY,
  rawRows = [],
  aggregation = "COUNT",
}) => {
  const [activeChart, setActiveChart] = useState("bar");
  const chartRef = useRef(null);

  const colors = useMemo(() => {
    return yValues.map((_, index) => {
      const hue = (index * 47) % 360;
      return `hsla(${hue}, 70%, 58%, 0.65)`;
    });
  }, [yValues]);

  const chartData = useMemo(
    () => ({
      labels: xValues.map((value) => String(value)),
      datasets: [
        {
          label: selectedY,
          data: yValues,
          backgroundColor: colors,
          borderColor: "rgba(145, 214, 255, 1)",
          borderWidth: 2,
          fill: activeChart === "line",
          tension: 0.35,
        },
      ],
    }),
    [activeChart, colors, selectedY, xValues, yValues]
  );

  const maxValue = yValues.length ? Math.max(...yValues) : 0;

  const getLegendLabel = useCallback(
    (index) => {
      if (aggregation === "NONE" && rawRows?.[index]?.x) {
        return rawRows[index].x;
      }

      return xValues[index];
    },
    [aggregation, rawRows, xValues]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              const item = items?.[0];

              if (!item) return "";

              const row = rawRows?.[item.dataIndex];

              if (aggregation === "NONE" && row?.x) {
                return row.x;
              }

              return item.label;
            },
            label: (context) => `${selectedY}: ${context.raw}`,
          },
        },
      },
      layout: {
        padding: 0,
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            color: "#ffffff",
            callback: function (value) {
              const label = this.getLabelForValue(value);
              return label.length > 18 ? `${label.slice(0, 18)}...` : label;
            },
          },
          grid: {
            color: "rgba(255, 255, 255, 0.08)",
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax: maxValue > 0 ? maxValue + maxValue * 0.15 : 10,
          ticks: {
            color: "#ffffff",
          },
          grid: {
            color: "rgba(255, 255, 255, 0.08)",
          },
        },
      },
    }),
    [aggregation, maxValue, rawRows, selectedY]
  );

  const exportChartWithLegend = useCallback(
    (filename = "dijagram-legenda.png") => {
      const chartInstance = chartRef.current;

      if (!chartInstance) return;

      chartInstance.update("none");

      const chartImage = chartInstance.toBase64Image("image/png", 1);
      const image = new Image();

      image.onload = () => {
        const padding = 28;
        const titleHeight = 46;
        const legendTitleHeight = 34;
        const rowHeight = 28;
        const maxLegendRows = Math.min(yValues.length, 80);
        const legendHeight = legendTitleHeight + maxLegendRows * rowHeight + 16;

        const width = Math.max(image.width, 900);
        const height = padding * 2 + titleHeight + image.height + 28 + legendHeight;

        const scale = 2;
        const exportCanvas = document.createElement("canvas");

        exportCanvas.width = width * scale;
        exportCanvas.height = height * scale;

        const ctx = exportCanvas.getContext("2d");

        if (!ctx) return;

        ctx.scale(scale, scale);

        ctx.fillStyle = "#2f294f";
        ctx.fillRect(0, 0, width, height);

        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 22px Arial";
        ctx.fillText(selectedY || "Dijagram", padding, padding + 24);

        ctx.drawImage(
          image,
          padding,
          padding + titleHeight,
          width - padding * 2,
          image.height
        );

        const legendStartY = padding + titleHeight + image.height + 30;

        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 17px Arial";
        ctx.fillText("Legenda", padding, legendStartY);

        ctx.font = "13px Arial";

        yValues.slice(0, maxLegendRows).forEach((value, index) => {
          const y = legendStartY + legendTitleHeight + index * rowHeight;
          const label = String(getLegendLabel(index));
          let text = `${label}: ${value}`;

          ctx.fillStyle = colors[index];
          ctx.fillRect(padding, y - 14, 16, 16);

          ctx.fillStyle = "#ffffff";

          const maxTextWidth = width - padding * 2 - 34;

          while (ctx.measureText(text).width > maxTextWidth && text.length > 12) {
            text = `${text.slice(0, -4)}...`;
          }

          ctx.fillText(text, padding + 28, y);
        });

        if (yValues.length > maxLegendRows) {
          ctx.fillStyle = "rgba(255, 255, 255, 0.75)";
          ctx.font = "italic 13px Arial";
          ctx.fillText(
            `Prikazano prvih ${maxLegendRows} stavki legende od ukupno ${yValues.length}.`,
            padding,
            height - padding
          );
        }

        const link = document.createElement("a");
        link.download = filename;
        link.href = exportCanvas.toDataURL("image/png");
        link.click();
      };

      image.src = chartImage;
    },
    [colors, getLegendLabel, selectedY, yValues]
  );

  useEffect(() => {
    const handleExport = (event) => {
      const filename = event?.detail?.filename || "dijagram-legenda.png";
      exportChartWithLegend(filename);
    };

    window.addEventListener("chart-export-png", handleExport);

    return () => {
      window.removeEventListener("chart-export-png", handleExport);
    };
  }, [exportChartWithLegend]);

  return (
    <div className="charts">
      <div className="chart-toggle">
        <h4>Tip grafikona</h4>

        <div className="chart-toggle-buttons">
          <button
            type="button"
            className={`chart-toggle-button ${activeChart === "bar" ? "active" : ""}`}
            onClick={() => setActiveChart("bar")}
          >
            Stubičasti
          </button>

          <button
            type="button"
            className={`chart-toggle-button ${activeChart === "line" ? "active" : ""}`}
            onClick={() => setActiveChart("line")}
          >
            Linijski
          </button>
        </div>
      </div>

      <div className="chart-canvas-wrap">
        {activeChart === "line" && (
          <Line ref={chartRef} data={chartData} options={options} />
        )}

        {activeChart === "bar" && (
          <Bar ref={chartRef} data={chartData} options={options} />
        )}
      </div>

      <details className="chart-legend-details">
        <summary>Legenda</summary>

        <div className="legend">
          {yValues.map((value, index) => {
            const label = getLegendLabel(index);

            return (
              <div key={`${label}_${index}`} className="legend-row">
                <div
                  className="legend-color"
                  style={{ backgroundColor: colors[index] }}
                />

                <span title={String(label)}>
                  {label}: {value}
                </span>
              </div>
            );
          })}
        </div>
      </details>
    </div>
  );
};

export default ChartsDisplay;